import Close from "@mui/icons-material/Close";
import { Tooltip } from "@mui/material";
import InsertVariable from "components/shared/InsertVariableMenu";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { PortalType } from "contexts/portal.context";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ICompany } from "store/settingsStore";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

import type { EmailTemplate } from "..";

interface IEmailTemplate {
    company: ICompany | undefined;
    email: { id?: number; name: string; content: any; status: "original" | "edited" } | undefined;
    index: number;
    isPressOrAudiovisual: boolean;
    handleEdit?: (
        ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number,
        type: "subject" | "body" | "cc",
    ) => void;
    emailTemplate: EmailTemplate[];
    setEmailTemplate: (emailTemplate: EmailTemplate[]) => void;
    onEmailsChanged: () => void;
    languageTab: "en" | "fr";
    hasLanguageChanged: boolean;
}

const EmailTemplates = ({
    company,
    email,
    index,
    isPressOrAudiovisual,
    emailTemplate,
    setEmailTemplate,
    onEmailsChanged,
    languageTab,
    hasLanguageChanged,
}: IEmailTemplate) => {
    const [t] = useTranslation();
    // For the variables button
    const [cursorPosition, setCursorPosition] = useState(email?.content?.body?.length ?? 0);

    const [emailBody, setEmailBody] = useState("");
    const [emails, setEmails] = useState<string[]>(email?.content?.mailCC ?? []);
    const [mailInput, setMailInput] = useState("");
    const portalType = useContext(PortalType);

    // Makes sure the email body is that of current email template.
    useEffect(() => {
        if (email?.name.endsWith(languageTab)) {
            setEmailBody(email?.content?.body ?? "");
        }
    }, [hasLanguageChanged]);

    const textFieldRef = useRef<HTMLTextAreaElement>(null);

    const isDocumentsReminder = !!email?.name.includes("documents_reminder");

    const variables = (publisherType: TPortalType | undefined): Record<string, string> => {
        if (isDocumentsReminder) {
            return {
                "{{name}}": t("EmailSettings.NameVariable"),
                "{{documents}}": t("EmailSettings.DocumentsVariable"),
            };
        }
        switch (publisherType) {
            default:
            case EPortalType.BOOK:
                return {
                    "{{name}}": t("EmailSettings.NameVariable"),
                    "{{title}}": t("EmailSettings.TitleVariable"),
                };
            case EPortalType.PRESS:
                return {
                    "{{name}}": t("EmailSettings.NameVariable"),
                    "{{title}}": t("EmailSettings.ArticleVariable"),
                    "{{parution}}": t("EmailSettings.ParutionVariable"),
                };
        }
    };

    const handleSelectClick = (cursorPosition: number) => {
        if (textFieldRef.current) {
            setCursorPosition(cursorPosition);
        }
    };

    const handleEditField = (ev: any, index: number, type: "subject" | "body") => {
        const newArray = structuredClone(emailTemplate);
        const targetSetting = company?.settings.find((e) => e.name === newArray[index].name);
        newArray[index].content[type] = ev.target ? ev.target.value : ev;
        if (
            targetSetting?.content.subject === newArray[index].content.subject &&
            targetSetting?.content.body === newArray[index].content.body
        ) {
            newArray[index].status = "original";
        } else {
            newArray[index].status = "edited";
        }
        setEmailBody(newArray[index].content["body"]);
        setEmailTemplate([...newArray]);
        onEmailsChanged();
    };

    const addEmail = (email: string) => {
        const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
        if (!emailRegex.test(email)) return;
        // Same deduped list for local chips and the saved template, so they can't drift apart
        const updatedEmails = Array.from(new Set([...emails, email]));
        setEmails(updatedEmails);
        setMailInput("");

        const newArray = structuredClone(emailTemplate);
        newArray[index].content["mailCC"] = updatedEmails;
        newArray[index].status = "edited";
        setEmailTemplate([...newArray]);
        onEmailsChanged();
    };

    const handleAddMail = (e: ChangeEvent<HTMLInputElement>) => {
        setMailInput(e.target.value);
        if (e.target.value.indexOf(",") < 0) return;
        addEmail(e.target.value.replace(/ /g, "").slice(0, -1));
    };

    const handleAddMailWithEnter = (e: KeyboardEvent<HTMLInputElement>) =>
        (e.key == "Enter" || e.code == "Space") && addEmail((e.target as HTMLInputElement).value.replace(/ /g, ""));

    const handleAddMailWithBlur = (e: any) => addEmail(e.target.value);

    const handleRemoveMail = (mailIndex: number) => {
        const remainingEmails = emails.filter((item, i) => i != mailIndex);
        setEmails(remainingEmails);
        const newArray = structuredClone(emailTemplate);
        newArray[index].content["mailCC"] = remainingEmails;
        newArray[index].status = "edited";
        setEmailTemplate([...newArray]);
        onEmailsChanged();
    };

    const cleanSettingName = email?.name.replace(`_${languageTab}`, "");

    const emailSubjectValue = useMemo(() => {
        if (email?.content.subject) {
            return email?.content.subject ?? "";
        } else {
            if (isDocumentsReminder) {
                return t("EmailSettings.DefaultObjectDocumentsReminder", { lng: languageTab });
            } else if (email?.name.includes("beneficiary")) {
                return t("EmailSettings.DefaultObjectConsolidated", { lng: languageTab });
            } else if (email?.name.includes("term")) {
                return t("EmailSettings.DefaultObjectByTitle", { lng: languageTab });
            }
        }
    }, [email, languageTab]);

    const emailBodyValue = useMemo(() => {
        if (emailBody) {
            return emailBody;
        } else {
            if (isDocumentsReminder) {
                return `${t("EmailSettings.DefaultBodyDocumentsReminder", { lng: languageTab })}${company?.name}`;
            } else if (email?.name.includes("beneficiary")) {
                return `${t("EmailSettings.DefaultBodyConsolidated", { lng: languageTab })}${company?.name}`;
            } else if (email?.name.includes("term")) {
                const mainText = !isPressOrAudiovisual
                    ? t("EmailSettings.DefaultBodyByTitle", { lng: languageTab })
                    : t(`EmailSettings.DefaultBodyByPublication_${portalType}`, { lng: languageTab });
                return mainText + company?.name;
            }
        }
    }, [emailBody, languageTab, email]);

    return (
        <div className="settings-email-content" key={index} style={{ marginBottom: "16px" }}>
            <div className="email-templates-top">
                <label className="settings-email-content-label">
                    {t(`EmailSettings.${cleanSettingName}_${portalType}`)}
                </label>
                <InsertVariable
                    cursorPosition={cursorPosition}
                    field={emailBody}
                    variables={variables(company?.publisherType)}
                    onSelect={(newValue: string) => {
                        setEmailBody(newValue);
                        handleEditField(newValue, index, "body");
                    }}
                />
            </div>
            <div className="settings-email-content-body">
                <div style={{ padding: "0 0 10px 0", fontWeight: "500" }}>
                    {t("EmailSettings.Of")} : noreply@crealo.app
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span className="label-object">{t("EmailSettings.Object")} :</span>
                    <StyledTextField
                        value={emailSubjectValue}
                        onChange={(e) => handleEditField(e, index, "subject")}
                        placeholder={t("EmailSettings.Object")}
                    ></StyledTextField>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span className="label-object">Cc :</span>
                    <div className="send-email-body-cc-mails" style={{ width: "100%" }}>
                        {emails.length > 0 &&
                            emails.map((item, i) => (
                                <div className="send-email-body-chip" key={i}>
                                    <div className="send-email-body-avatar">{item.slice(0, 1).toLocaleUpperCase()}</div>
                                    <Tooltip title={item} arrow>
                                        <div className="send-email-body-text">{item.split("@")[0]}</div>
                                    </Tooltip>
                                    <div className="send-email-body-close" onClick={() => handleRemoveMail(i)}>
                                        <Close />
                                    </div>
                                </div>
                            ))}
                        <input
                            type="text"
                            className="send-email-body-cc-mail-input"
                            value={mailInput}
                            onChange={handleAddMail}
                            onKeyDown={handleAddMailWithEnter}
                            onBlur={handleAddMailWithBlur}
                        />
                    </div>
                </div>
                <textarea
                    ref={textFieldRef}
                    // label={null}
                    value={emailBodyValue}
                    onChange={(e: any) => handleEditField(e, index, "body")}
                    onClick={() =>
                        handleSelectClick(
                            textFieldRef.current ? (textFieldRef.current.selectionStart as number) : emailBody?.length,
                        )
                    }
                    onKeyUp={() =>
                        handleSelectClick(
                            textFieldRef.current ? (textFieldRef.current.selectionStart as number) : emailBody?.length,
                        )
                    }
                    placeholder={t("EmailSettings.Body")}
                />
            </div>
        </div>
    );
};

export default EmailTemplates;
